import { type DragEvent, type DragEventHandler, useCallback, useRef, useState } from 'react';

interface UseFileDropzoneParams {
  /** Called with the dropped files when a drop occurs while enabled. */
  onFiles: (files: FileList) => void;
  /**
   * When true, no files are delivered and the hover state never activates.
   * `onDrop` still calls preventDefault/stopPropagation so the browser doesn't
   * navigate to a dropped file, but it delivers nothing.
   */
  disabled?: boolean;
}

interface FileDropzone {
  /** True while a drag is hovering over the target (for active styling). */
  isDraggingOver: boolean;
  /** Handlers to spread onto the drop target element. */
  dropzoneProps: {
    onDragEnter: DragEventHandler<HTMLElement>;
    onDragLeave: DragEventHandler<HTMLElement>;
    onDragOver: DragEventHandler<HTMLElement>;
    onDrop: DragEventHandler<HTMLElement>;
  };
}

/**
 * Headless drag-and-drop file target. Owns the drag-hover state and the four
 * native drag handlers (with a depth counter so nested elements don't flicker
 * the hover state), delivering the dropped FileList to `onFiles`. Callers keep
 * their own validation, rendering, and value model — this hook only handles the
 * raw drag/drop mechanics shared across file inputs.
 */
export const useFileDropzone = ({ onFiles, disabled = false }: UseFileDropzoneParams): FileDropzone => {
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const dragDepthRef = useRef<number>(0);

  const onDragEnter = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (disabled) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      dragDepthRef.current += 1;
      setIsDraggingOver(true);
    },
    [disabled]
  );

  const onDragLeave = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (disabled) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      dragDepthRef.current -= 1;
      if (dragDepthRef.current <= 0) {
        dragDepthRef.current = 0;
        setIsDraggingOver(false);
      }
    },
    [disabled]
  );

  const onDragOver = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (disabled) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = 'copy';
    },
    [disabled]
  );

  const onDrop = useCallback(
    (event: DragEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (disabled) {
        return;
      }
      dragDepthRef.current = 0;
      setIsDraggingOver(false);
      onFiles(event.dataTransfer.files);
    },
    [disabled, onFiles]
  );

  return {
    isDraggingOver,
    dropzoneProps: { onDragEnter, onDragLeave, onDragOver, onDrop },
  };
};
